"use client";

import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { Package, Sparkles, UserRound } from "lucide-react";
import { type UserDetailsFragment } from "@/gql/graphql";
import { LinkWithChannel } from "@/ui/atoms/link-with-channel";
import { cn } from "@/lib/utils";

type Props = {
	user: UserDetailsFragment | null;
	unavailable?: boolean;
};

function AccountTile({
	href,
	icon: Icon,
	title,
	subtitle,
	emphasized = false,
}: {
	href: string;
	icon: React.ComponentType<{ className?: string }>;
	title: string;
	subtitle?: string;
	emphasized?: boolean;
}) {
	return (
		<LinkWithChannel
			href={href}
			className={cn(
				"flex flex-1 flex-col items-center gap-1.5 rounded-2xl border px-2 py-3 text-center transition-colors",
				emphasized
					? "border-primary/25 bg-primary/[0.07] hover:bg-primary/[0.12]"
					: "border-foreground/[0.08] bg-background/60 hover:bg-muted/70",
			)}
		>
			<span
				className={cn(
					"flex h-8 w-8 items-center justify-center rounded-full",
					emphasized ? "bg-primary/15" : "bg-primary/10",
				)}
			>
				<Icon className="h-4 w-4 text-primary" />
			</span>
			<span className="text-[12.5px] font-semibold leading-tight text-foreground">{title}</span>
			{subtitle && (
				<span
					className={cn(
						"text-[11px] leading-snug",
						emphasized ? "font-medium text-primary" : "text-muted-foreground",
					)}
				>
					{subtitle}
				</span>
			)}
		</LinkWithChannel>
	);
}

/**
 * Account entry point inside the mobile menu — the only login/orders access
 * on viewports below `lg` (the desktop user menu is hidden there).
 *
 * Guests get a split: sign-in for existing customers, plus a registration
 * invitation carrying the Bergdorf Points welcome bonus.
 */
export function MobileAccountSection({ user, unavailable = false }: Props) {
	const t = useTranslations("nav");
	const tAuth = useTranslations("auth");
	const tSignup = useTranslations("signup");
	const params = useParams<{ locale?: string; channel?: string }>();
	const base = `/${params.locale ?? "en"}/${params.channel ?? "eu"}`;

	if (unavailable) {
		return (
			<section aria-label={t("accountAria")} className="mt-4 flex gap-2">
				<AccountTile href="/account" icon={UserRound} title={t("myAccount")} />
			</section>
		);
	}

	if (user) {
		return (
			<section aria-label={t("accountAria")} className="mt-4">
				<div className="flex items-baseline justify-between gap-3 px-1.5">
					<p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
						{t("accountAria")}
					</p>
					<p className="min-w-0 truncate text-[11px] text-muted-foreground">{user.email}</p>
				</div>
				<div className="mt-2 flex gap-2">
					<AccountTile href="/account" icon={UserRound} title={t("myAccount")} />
					<AccountTile href="/orders" icon={Package} title={t("myOrders")} />
					<AccountTile href="/account/loyalty" icon={Sparkles} title={t("bergdorfPoints")} />
				</div>
			</section>
		);
	}

	return (
		<section aria-label={t("accountAria")} className="mt-4">
			<p className="px-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
				{t("accountAria")}
			</p>
			<div className="mt-2 flex gap-2">
				<AccountTile
					href={`/login?next=${encodeURIComponent(`${base}/account`)}`}
					icon={UserRound}
					title={tAuth("signIn")}
					subtitle={t("signInHint")}
				/>
				<AccountTile
					href="/signup"
					icon={Sparkles}
					title={tSignup("createAccount")}
					subtitle={t("signupBonusHint")}
					emphasized
				/>
			</div>
			<p className="mt-2 px-1.5 text-[11px] leading-snug text-muted-foreground">{t("accountBenefitsHint")}</p>
		</section>
	);
}
